import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";

import { ENV_CONFIG } from "../../bin/env.config";
import { ErrorHandlerService } from "./error-handler.service";
import { Injectable } from "@angular/core";
import { Observable, Subject, throwError } from "rxjs";
import { SalonmonsterHttpClient } from "../services/salonmonster-http-client";
import { UserService } from "./user.service";
import { map, tap } from "rxjs/operators";
import { Connection, ConnectionPermissions, ConnectionRequest } from "../models";

@Injectable({
  providedIn: "root",
})
export class ConnectionService extends SalonmonsterHttpClient {

  /**
   * Fires when the set of live connections changes — a request was accepted
   * (which creates both directions) or a connection was removed. Consumers
   * that merge connected stylists into their own lists rebuild on this.
   */
  private readonly connectionsChanged = new Subject<void>();

  public readonly connectionsChanged$: Observable<void> = this.connectionsChanged.asObservable();

  /**
   * Fires when the set of connection requests changes — created, accepted,
   * rejected, cancelled or deleted. Drives the side-menu pending badge.
   *
   * Emitted from inside the mutation methods rather than by their callers, so
   * a future caller cannot forget to signal. `tap` only runs on a successful
   * emission, so a failed request signals nothing.
   */
  private readonly connectionRequestsChanged = new Subject<void>();

  public readonly connectionRequestsChanged$: Observable<void> = this.connectionRequestsChanged.asObservable();

  constructor(
    http: HttpClient,
    userService: UserService,
    errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);
  }

  /**
   * Every live connection for the signed-in stylist.
   *
   * Each row carries `counterpartyCanConnect` — the API's own spelling, and
   * the only signal the app gets that the colleague on the other end no
   * longer passes the server's connect-accounts gate (revoked, deactivated
   * or deleted). Nothing compiles the two repos together, so a misspelling
   * here reads `undefined` in silence; `Connection.parse` owns the name and
   * treats an absent value as connectable, which is what keeps this working
   * against an API that predates the field.
   */
  getConnections(): Observable<Array<Connection>> {
    const url = `${ENV_CONFIG.API_ROOT}/connections`;
    return this.get(url).pipe(
      map((data) => {
        let connections: Array<Connection> = [];
        for (const connectionData of data) {
          connections.push(Connection.parse(connectionData));
        }

        return connections;
      })
    );
  }

  getConnectionRequests(): Observable<Array<ConnectionRequest>> {
    const url = `${ENV_CONFIG.API_ROOT}/connection-requests`;
    return this.get(url).pipe(
      map((data) => {
        let connectionRequests: Array<ConnectionRequest> = [];
        for (const connectionRequestData of data) {
          connectionRequests.push(ConnectionRequest.parse(connectionRequestData));
        }
        return connectionRequests;
      })
    );
  }

  /**
   * `grantPermissions` is the exact field name the API reads for what the
   * sender shares. The two ship from separate repos, so a rename here fails
   * silently: the API stores its default while the panel reports what the
   * user chose. Left undefined, the API applies its own default.
   */
  createConnectionRequest(
    fromStylistID: number,
    toStylistID: number,
    email: string,
    grantPermissions?: ConnectionPermissions
  ): Observable<ConnectionRequest> {
    const url = `${ENV_CONFIG.API_ROOT}/connection-requests`;
    return this.post(url, {
      fromStylistID: fromStylistID,
      toStylistID: toStylistID,
      toEmail: email,
      grantPermissions: grantPermissions,
    }).pipe(
      map((data) => {
        return data;
      }),
      // The signal means "the request set changed", not "your badge changed" —
      // a request you sent cannot move your own recipient-scoped count.
      tap(() => this.connectionRequestsChanged.next())
    );
  }

  /**
   * `grantPermissions` is the recipient's own choice of what the sender may
   * do, and rides only on an accept — that is the one transition that writes
   * a connection row. Same field name as createConnectionRequest, for the
   * same reason: the API reads exactly this name and applies its default to
   * a missing one, so an accept must never reach here without a triple.
   * Reject and cancel carry no grant; there is nothing for one to land on.
   */
  updateConnectionRequest(
    id: number,
    stylistID: number,
    status: string,
    grantPermissions?: ConnectionPermissions
  ): Observable<ConnectionRequest> {
    const url = `${ENV_CONFIG.API_ROOT}/connection-requests/${id}`;
    const body: Record<string, any> = { id: id, stylistID: stylistID, status: status };
    if (status === 'accepted') {
      // Enforce the contract rather than documenting it. An undefined triple
      // is dropped by JSON.stringify, so the request would be indistinguishable
      // from one that never chose anything and the API would apply its default
      // grant — the blind grant this whole feature exists to replace. No live
      // caller does this; the guard is for the next accept path added.
      if (!grantPermissions) {
        return throwError(() => new Error('An accept must carry the permissions the recipient chose.'));
      }
      body['grantPermissions'] = grantPermissions;
    }
    return this.put(url, body).pipe(
      map((data) => {
        return data;
      }),
      tap(() => {
        this.connectionRequestsChanged.next();
        // Only an accept creates connections; a reject or cancel leaves the
        // connection set untouched, so signalling it would make every
        // declined request refetch /connections for no visible change.
        if (status === 'accepted') {
          this.connectionsChanged.next();
        }
      })
    );
  }

  deleteConnectionRequest(id: number, stylistID: number): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/connection-requests/${id}`;
    return this.delete(url, {id: id, stylistID: stylistID}).pipe(
      map((data) => {
        return data;
      }),
      tap(() => this.connectionRequestsChanged.next())
    );
  }

  updateConnection(updateConnection: Connection): Observable<Connection> {
    const url = `${ENV_CONFIG.API_ROOT}/connections/${updateConnection.id}`;
    // Deliberately signals nothing. This writes permissions and the calendar
    // display preference, and no current consumer of connectionsChanged$ reads
    // either — the staff pickers list every live connection regardless. That
    // holds only while the picker ignores grantedPermissions; the deferred
    // permission-aware filtering work is what would need a signal here.
    // Two server-derived, read-only fields are stripped before the write.
    //
    // counterpartyCanConnect says whether
    // the colleague currently passes the server's own gate. It stays on the
    // model (clone() and the picker read it) but must not travel on a write —
    // echoing a derived value back under its own name is exactly what the
    // Stylist model in this same change goes out of its way to prevent, and
    // the only reason it is harmless here today is that the API happens not to
    // have a column of that name to write it to.
    //
    // grantedPermissions is the FAR half of the pair — what the counterparty
    // granted you. Only the near half (grantPermissions) is yours to set, and
    // Connection.update on the API reads only that, scoped to your own row, so
    // sending the far half is inert today. It still should not travel on a
    // write: a client does not get to state what it was granted.
    const {
      counterpartyCanConnect,
      grantedPermissions,
      ...writableConnection
    } = updateConnection.toJSON();
    return this.put(url, {updatedConnection: writableConnection}).pipe(
      map((data) => {
        return data;
      })
    );
  }

  deleteConnection(id: number): Observable<boolean> {
    const url = `${ENV_CONFIG.API_ROOT}/connections/${id}`;
    return this.delete(url, {id: id}).pipe(
      map((data) => {
        return data;
      }),
      tap(() => this.connectionsChanged.next())
    );
  }

  getConnectionByStylistID(stylistID: number): Observable<Connection> {
    const url = `${ENV_CONFIG.API_ROOT}/connections/stylist/${stylistID}`;
    return this.get(url).pipe(
      map((data) => {
        if (!data || data.length === 0) {
          throw new Error('Connection not found');
        }
        return Connection.parse(data[0]);
      })
    );
  }
}